import { parseCreatedTask } from "./task-schedule";
import { supportedReasoningEffort } from "./reasoning";
import type { Attachment, ChatMessage, Conversation, WorkspaceState } from "../types";
import { DEFAULT_MODEL_ID, FALLBACK_MODELS } from "./models";
import { DEFAULT_IMAGE_MODEL_ID, isImageModelId } from "./image-models";
import { downloadResource } from "./resource-cache";

const invalid = (): never => { throw new Error("invalid_backup"); };
const object = (value: unknown): Record<string, unknown> => value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : invalid();
const text = (value: unknown): string => typeof value === "string" ? value : invalid();
const array = (value: unknown): unknown[] => Array.isArray(value) ? value : invalid();
const date = (value: unknown): string => Number.isFinite(Date.parse(text(value))) ? value as string : invalid();
const number = (value: unknown): number => typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : invalid();
const optional = (value: unknown, check: (value: unknown) => unknown) => { if (value !== undefined) check(value); };
const safeUrl = (value: unknown, media: boolean): string => {
  const url = text(value);
  if (media && (/^data:(?:image\/(?:png|jpeg|webp|gif)|application\/(?:pdf|json)|text\/(?:plain|markdown|csv))(?:;charset=[\w-]+)?;base64,[a-zA-Z0-9+/]*={0,2}$/.test(url) || /^\/api\/(?:image-jobs|generated-files)\/[a-zA-Z0-9_./?=&%-]+$/.test(url))) return url;
  if (!media) {
    try { if (["http:", "https:"].includes(new URL(url).protocol)) return url; } catch { /* Reject malformed or executable URLs. */ }
  }
  return invalid();
};
const validateAttachment = (value: unknown): Attachment => {
  const item = object(value);
  text(item.id); text(item.name); text(item.mimeType); number(item.size);
  if (item.kind === "image") safeUrl(item.dataUrl, true);
  else if (item.kind === "file") text(item.text);
  else invalid();
  return item as unknown as Attachment;
};
const validateMessage = (value: unknown, interruptedNotice: string): ChatMessage => {
  const item = object(value);
  text(item.id); text(item.content); date(item.createdAt);
  if (item.role !== "user" && item.role !== "assistant") invalid();
  optional(item.reasoning, text); optional(item.error, text); optional(item.elapsedMs, number); optional(item.modelId, text);
  optional(item.errorCode, text); optional(item.retryAt, date);
  if (item.status !== undefined && !["streaming", "complete", "error"].includes(text(item.status))) invalid();
  optional(item.attachments, (value) => array(value).forEach(validateAttachment));
  optional(item.generatedImages, (value) => array(value).forEach((raw) => {
    const image = object(raw); text(image.id); safeUrl(image.dataUrl, true); text(image.modelId); text(image.modelName); text(image.prompt); number(image.width); number(image.height);
    optional(image.elapsedMs, number); optional(image.sourceImageIds, (ids) => array(ids).forEach(text));
    optional(image.expiresAt, date);
  }));
  optional(item.browserScreenshots, (value) => array(value).forEach((raw) => {
    const image = object(raw); text(image.id); safeUrl(image.dataUrl, true); safeUrl(image.url, false); text(image.title); number(image.width); number(image.height);
    optional(image.expiresAt, date);
  }));
  optional(item.generatedFiles, (value) => array(value).forEach((raw) => {
    const file = object(raw); text(file.id); text(file.fileName); text(file.mimeType); number(file.size); safeUrl(file.downloadUrl, true);
    if (!["txt", "md", "pdf", "csv", "json"].includes(text(file.format))) invalid();
    optional(file.expiresAt, date); optional(file.elapsedMs, number);
  }));
  optional(item.webSources, (value) => array(value).forEach((raw) => {
    const source = object(raw); safeUrl(source.url, false); text(source.title); text(source.domain); optional(source.accessedAt, date); optional(source.index, number);
  }));
  optional(item.imageGeneration, (value) => {
    const state = object(value); text(state.status); text(state.modelId); text(state.modelName); optional(state.message, text); optional(state.prompt, text);
  });
  optional(item.webResearch, (value) => {
    const state = object(value); text(state.status); optional(state.message, text); optional(state.query, text); optional(state.url, (url) => safeUrl(url, false));
  });
  optional(item.createdTasks, value => array(value).forEach(task => { if (!parseCreatedTask(task)) invalid(); }));
  optional(item.scriptExecutions, (value) => array(value).forEach((raw) => {
    const run = object(raw); text(run.id); text(run.title); text(run.code);
    if (!["python", "javascript"].includes(text(run.language)) || !["running", "complete", "error"].includes(text(run.status))) invalid();
    optional(run.stdout, text); optional(run.stderr, text); optional(run.elapsedMs, number);
    if (run.truncated !== undefined && typeof run.truncated !== "boolean") invalid();
  }));
  const result = { ...item, generationSessionId: undefined, streamCursor: undefined, recoveryState: undefined, modelProgress: undefined } as unknown as ChatMessage;
  if (result.status === "streaming") { result.status = "error"; result.error = interruptedNotice; }
  if (result.imageGeneration?.status === "generating") result.imageGeneration = { ...result.imageGeneration, status: "error", jobId: undefined, message: interruptedNotice };
  if (result.scriptExecutions) result.scriptExecutions = result.scriptExecutions.map((run) => run.status === "running" ? { ...run, status: "error", stderr: run.stderr || interruptedNotice } : run);
  return result;
};

export const importWorkspaceBackup = (json: string, interruptedNotice: string): WorkspaceState => {
  // Never accept internal asset references or prototype-like keys from portable files.
  const parsed: unknown = JSON.parse(json, (key, value: unknown) => {
    if (["__proto__", "constructor", "prototype", "$assetRef"].includes(key)) invalid();
    return value;
  });
  const root = object(parsed);
  if (root.format !== "neurondeck-workspace" || root.version !== 1) invalid();
  const state = object(root.workspace);
  const conversations = array(state.conversations);
  if (!conversations.length || conversations.length > 10_000) invalid();
  const modelIds = new Set(FALLBACK_MODELS.map((model) => model.id));
  const ids = new Map(conversations.map((raw) => [text(object(raw).id), crypto.randomUUID()]));
  if (ids.size !== conversations.length) invalid();
  const restored = conversations.map((raw): Conversation => {
    const item = object(raw);
    if (item.messagesLoaded === false) invalid();
    const modelId = modelIds.has(text(item.modelId)) ? item.modelId as string : DEFAULT_MODEL_ID;
    const messages = array(item.messages).map((message) => validateMessage(message, interruptedNotice));
    if (new Set(messages.map((message) => message.id)).size !== messages.length) invalid();
    let draft: Conversation["draft"];
    if (item.draft !== undefined) {
      const value = object(item.draft);
      draft = { content: text(value.content), attachments: array(value.attachments).map(validateAttachment) };
      if (value.beforeEdit !== undefined) {
        const previous = object(value.beforeEdit);
        draft.beforeEdit = { content: text(previous.content), attachments: array(previous.attachments).map(validateAttachment) };
      }
      if (value.editingMessageId !== undefined && messages.some((message) => message.id === value.editingMessageId && message.role === "user")) draft.editingMessageId = text(value.editingMessageId);
    }
    return {
      id: ids.get(text(item.id))!, title: text(item.title), modelId, systemPrompt: text(item.systemPrompt),
      ...(item.titleSource === "pending" || item.titleSource === "ai" || item.titleSource === "manual" ? { titleSource: item.titleSource } : {}),
      temperature: Math.min(2, number(item.temperature)),
      reasoningEffort: supportedReasoningEffort(modelId, item.reasoningEffort),
      imageModelId: isImageModelId(item.imageModelId) ? item.imageModelId : DEFAULT_IMAGE_MODEL_ID,
      messages, createdAt: date(item.createdAt), updatedAt: date(item.updatedAt), ...(draft ? { draft } : {}),
      ...(item.pinned === true ? { pinned: true } : {}),
      ...(item.archivedAt !== undefined ? { archivedAt: date(item.archivedAt) } : {}),
      ...(item.deletedAt !== undefined ? { deletedAt: date(item.deletedAt) } : {}),
      ...(typeof item.parentConversationId === "string" && ids.has(item.parentConversationId) ? { parentConversationId: ids.get(item.parentConversationId)! } : {}),
      ...(typeof item.rootConversationId === "string" && ids.has(item.rootConversationId) ? { rootConversationId: ids.get(item.rootConversationId)! } : {}),
      ...(item.forkMessageId !== undefined ? { forkMessageId: text(item.forkMessageId) } : {}),
      ...(item.forkIndex !== undefined ? { forkIndex: number(item.forkIndex) } : {}),
    };
  });
  const favoriteModelIds = array(state.favoriteModelIds).map(text).filter((id) => modelIds.has(id));
  return { conversations: restored, activeConversationId: ids.get(String(state.activeConversationId)) ?? restored[0].id, favoriteModelIds, ...(state.recentModelIds !== undefined ? { recentModelIds: array(state.recentModelIds).map(text).filter((id) => modelIds.has(id)).slice(0, 8) } : {}) };
};

export const createWorkspaceBackup = async (workspace: WorkspaceState): Promise<{ json: string; unavailableAssets: number }> => {
  if (workspace.conversations.some((conversation) => conversation.messagesLoaded === false)) throw new Error("workspace_not_hydrated");
  let unavailableAssets = 0;
  const downloads = new Map<string, Promise<string>>();
  const download = (url: string): Promise<string> => {
    if (!downloads.has(url)) downloads.set(url, (async () => {
      try {
        return await downloadResource(url);
      } catch { unavailableAssets += 1; return url; }
    })());
    return downloads.get(url)!;
  };
  const visit = async (value: unknown): Promise<unknown> => {
    if (!value || typeof value !== "object") return value;
    if (Array.isArray(value)) {
      const result: unknown[] = [];
      for (const item of value) result.push(await visit(item));
      return result;
    }
    const entries: Array<[string, unknown]> = [];
    for (const [key, item] of Object.entries(value)) {
      entries.push([key, typeof item === "string" && ["dataUrl", "downloadUrl"].includes(key) && /^\/api\/(?:image-jobs|generated-files)\//.test(item) ? await download(item) : await visit(item)]);
    }
    const result = Object.fromEntries(entries);
    if (typeof result.downloadUrl === "string" && result.downloadUrl.startsWith("data:")) delete result.expiresAt;
    return result;
  };
  const copy = await visit(workspace);
  return { json: JSON.stringify({ format: "neurondeck-workspace", version: 1, exportedAt: new Date().toISOString(), unavailableAssets, workspace: copy }), unavailableAssets };
};
